
import { Component, Vue, Watch } from 'vue-property-decorator'
import { AppModule } from '@/store/module'
import { BusinessCategory } from './types'
import NavSidebar from './components/NavSidebar.vue'
import MobileTags from './components/MobileTags.vue'
import ContentSection from './components/ContentSection.vue'
import HeroBanner from './components/HeroBanner.vue'
import ContactBlock from '@/components/ContactBlock.vue'
import HelpBlock from '@/components/HelpBlock.vue'
import FooterBlock from '@/components/FooterBlock.vue'
import Breadcrumb from '@/components/Breadcrumb.vue'

@Component({
  components: {
    NavSidebar,
    MobileTags,
    ContentSection,
    HeroBanner,
    ContactBlock,
    HelpBlock,
    FooterBlock,
    Breadcrumb,
  },
})
export default class BusinessIntroPage extends Vue {
  // Breadcrumb navigation items
  breadcrumbItems = [{ label: '首頁', to: '/' }, { label: '智慧Pos方案' }]

  // Data loaded from data.json
  private categories: BusinessCategory[] = []

  // Currently highlighted navigation item ID
  private activeEntryId = ''

  // Navigation bar height constant
  private readonly navHeight: number = 50

  // IntersectionObserver instance (to be used in task 8.4)
  private observer: IntersectionObserver | null = null

  // Flag to prevent ScrollSpy / ScrollTo conflicts (to be used in task 8.2)
  private isScrollingToEntry = false

  // Scroll end timer (to be used in task 8.2)
  private scrollEndTimer: number | null = null

  /** Read viewport mode from AppModule */
  get isH5(): boolean {
    return AppModule.isH5
  }

  created(): void {
    this.loadData()
  }

  mounted(): void {
    this.initScrollSpy()
  }

  beforeDestroy(): void {
    this.destroyScrollSpy()
    if (this.scrollEndTimer !== null) {
      clearTimeout(this.scrollEndTimer)
    }
  }

  @Watch('isH5')
  onIsH5Change(): void {
    this.destroyScrollSpy()
    this.$nextTick(() => {
      this.initScrollSpy()
    })
  }

  /** Load content data from data.json */
  private loadData(): void {
    try {
      const data = require('./data.json') as { categories: BusinessCategory[] }
      this.categories = data.categories || []
    } catch (error) {
      console.error('Failed to load business intro data:', error)
      this.categories = []
    }
  }

  /** Handle navigation item click: update active entry and scroll to it. */
  handleEntryClick(entryId: string): void {
    this.activeEntryId = entryId
    this.scrollToEntry(entryId)
  }

  /** Scroll smoothly to the content entry identified by entryId. */
  private scrollToEntry(entryId: string): void {
    // Locate target element via data-entry-id attribute
    const element = document.querySelector(`[data-entry-id="${entryId}"]`)
    if (!element) return

    this.isScrollingToEntry = true

    const elementTop = element.getBoundingClientRect().top + window.scrollY
    let offset = this.navHeight + 16 // nav bar 50px + 16px buffer

    if (this.isH5) {
      // Mobile: additionally subtract MobileTags height
      const tagsEl = document.querySelector('.mobile-tags') as HTMLElement | null
      offset += tagsEl ? tagsEl.offsetHeight : 0
    }

    window.scrollTo({ top: elementTop - offset, behavior: 'smooth' })

    if (this.scrollEndTimer !== null) clearTimeout(this.scrollEndTimer)
    this.scrollEndTimer = window.setTimeout(() => {
      this.isScrollingToEntry = false
    }, 800)
  }

  /** Initialize IntersectionObserver to watch all [data-entry-id] elements. */
  private initScrollSpy(): void {
    // Graceful degradation: skip if IntersectionObserver is not supported
    if (typeof IntersectionObserver === 'undefined') return

    const tagsEl = document.querySelector('.mobile-tags') as HTMLElement | null
    const tagsHeight = tagsEl ? tagsEl.offsetHeight : 52

    const options: IntersectionObserverInit = {
      root: null,
      rootMargin: this.isH5
        ? `-${this.navHeight + tagsHeight}px 0px -70% 0px`
        : `-${this.navHeight}px 0px -80% 0px`,
      threshold: 0
    }

    // Track visibility of each entry for determining closest to top
    const entryVisibility = new Map<string, { top: number }>()

    this.observer = new IntersectionObserver((entries) => {
      if (this.isScrollingToEntry) return

      // Update visibility tracking for each entry
      entries.forEach(entry => {
        const id = entry.target.getAttribute('data-entry-id')
        if (id) {
          if (entry.isIntersecting) {
            entryVisibility.set(id, { top: entry.boundingClientRect.top })
          } else {
            entryVisibility.delete(id)
          }
        }
      })

      // Find the visible entry closest to the top of the viewport
      let closestId: string | null = null
      let closestTop = Infinity

      entryVisibility.forEach((data, id) => {
        if (data.top >= 0 && data.top < closestTop) {
          closestId = id
          closestTop = data.top
        }
      })

      if (closestId && closestId !== this.activeEntryId) {
        this.activeEntryId = closestId
      }
    }, options)

    // Observe all data-entry-id elements
    document.querySelectorAll('[data-entry-id]').forEach(el => {
      this.observer?.observe(el)
    })
  }

  /** Disconnect and clear the IntersectionObserver. */
  private destroyScrollSpy(): void {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    }
  }
}
